import {
  DestinationEntityRecordStatus,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type MigratedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand, PatientRelationship } from '@principle-theorem/principle-core';
import {
  PatientRelationshipType,
  type IBrand,
  type IPatient,
  type IPatientRelationship,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  PrimaryKey,
  asDocRef,
  find$,
  getError,
  resolveSequentially,
  snapshot,
  toNamedDocument,
  toTimestamp,
  where,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { map } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import {
  IPatientDestinationRecord,
  PATIENT_RESOURCE_TYPE,
} from '../../../destination/entities/patient';
import {
  BasePatientRelationshipDestinationEntity,
  type IPatientRelationshipJobData as IPatientRelationshipBaseJobData,
} from '../../../destination/entities/patient-relationship';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  PatientSourceEntity,
  PraktikaPatient,
} from '../../source/entities/patient';
import {
  PatientRelationshipSourceEntity,
  PraktikaPatientRelationshipTypes,
  type IPraktikaPatientRelationship,
} from '../../source/entities/patient-relationship';
import { PatientDestinationEntity } from './patients';

export const PATIENT_RELATIONSHIP_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: 'patientRelationships',
    label: 'Patient Relationships',
    description: '',
  },
});

export interface IPatientRelationshipDestinationRecord {
  patientRef: PrimaryKey<IPatient>;
}

export type IPatientRelationshipJobData =
  IPatientRelationshipBaseJobData<PraktikaPatient>;

export class PatientRelationshipDestinationEntity extends BasePatientRelationshipDestinationEntity<
  PraktikaPatient,
  IPatientRelationshipDestinationRecord,
  IPatientRelationshipJobData
> {
  destinationEntity = PATIENT_RELATIONSHIP_DESTINATION_ENTITY;

  patientSourceEntity = new PatientSourceEntity();
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientRelationships: new PatientRelationshipSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  buildMigrationData(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ):
    | IPatientRelationshipJobData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord) {
    return data;
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    data: IPatientRelationshipJobData
  ): Promise<IDestinationEntityRecord> {
    const relationships = await this._getValidRelationships(
      data.sourcePatient,
      migration
    );

    const sourcePatientId = data.sourcePatient.data.patient_id.toString();
    const patientRef = await translationMapHandler.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    try {
      await this._addRelationshipsToPatient(
        relationships,
        translationMapHandler,
        data.brand,
        patientRef
      );
      return this._buildSuccessResponse(data.sourcePatient.record);
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  private _buildSuccessResponse(
    patient: Pick<
      IGetRecordResponse<PraktikaPatient>['record'],
      'label' | 'uid' | 'id'
    >
  ): IDestinationEntityRecord<IPatientRelationshipDestinationRecord> {
    return {
      uid: patient.uid,
      label: patient.label,
      data: {
        patientRef: patient.id,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.id,
      migratedAt: toTimestamp(),
    };
  }

  private async _addRelationshipsToPatient(
    praktikaRelationships: IPraktikaPatientRelationship[],
    translationMap: TranslationMapHandler,
    brand: WithRef<IBrand>,
    patientRef: DocumentReference<IPatient>
  ): Promise<void> {
    const relationships = compact(
      await resolveSequentially(praktikaRelationships, async (guarantor) => {
        if (!guarantor.person_id) {
          throw new Error('No person_id');
        }
        const guarantorRef = await translationMap.getDestination<IPatient>(
          guarantor.person_id.toString(),
          PATIENT_RESOURCE_TYPE
        );
        if (!guarantorRef) {
          throw new Error(`No ref for ${guarantor.person_id}`);
        }
        return getPatientRelationship(
          brand,
          guarantor,
          asDocRef<IPatient>(guarantorRef)
        );
      })
    );
    if (relationships.length) {
      await FirestoreMigrate.patchDoc(patientRef, {
        relationships,
      });

      const patient = await Firestore.getDoc(patientRef);
      await PatientRelationship.addRelationshipBackReferences(patient);
    }
  }

  private async _getValidRelationships(
    sourcePatient: IGetRecordResponse<PraktikaPatient>,
    migration: WithRef<IPracticeMigration>
  ): Promise<IPraktikaPatientRelationship[]> {
    const relationships =
      await this.sourceEntities.patientRelationships.filterRecords(
        migration,
        'patientId',
        sourcePatient.data.patient_id.toString()
      );
    return relationships
      .filter(
        (guarantor) =>
          guarantor.data.person_id &&
          sourcePatient.data.patient_id.toString() !==
            guarantor.data.person_id.toString() &&
          guarantor.data.is_person
      )
      .map((relationship) => relationship.data);
  }
}

function getPatientRelationship(
  brand: WithRef<IBrand>,
  relationship: IPraktikaPatientRelationship,
  patientRef: DocumentReference<IPatient>
): Promise<IPatientRelationship | undefined> {
  return snapshot(
    find$(Brand.patientCol(brand), where('ref', '==', patientRef)).pipe(
      map((destinationPatient) => {
        if (!destinationPatient) {
          return;
        }
        const patient = toNamedDocument(destinationPatient);

        return buildRelationship(relationship, patient);
      })
    )
  );
}

export interface IPatientCombinedData {
  sourcePatient: IGetRecordResponse<PraktikaPatient>;
  record: WithRef<IDestinationEntityRecord<IPatientDestinationRecord>> &
    MigratedDestinationEntityRecord<IPatientDestinationRecord>;
}

function buildRelationship(
  relationship: IPraktikaPatientRelationship,
  patient: INamedDocument<IPatient>
): IPatientRelationship | undefined {
  switch (relationship.relationship_type_id) {
    case PraktikaPatientRelationshipTypes.Unknown:
    case PraktikaPatientRelationshipTypes.Carer:
    case PraktikaPatientRelationshipTypes.Employee:
    case PraktikaPatientRelationshipTypes.Beneficiary:
    case PraktikaPatientRelationshipTypes.InsuredPlaintiff:
    case PraktikaPatientRelationshipTypes.Grandchild:
    case PraktikaPatientRelationshipTypes.Grandparent:
    case PraktikaPatientRelationshipTypes.Dependent:
      return {
        patient,
        type: PatientRelationshipType.Unknown,
      };
    case PraktikaPatientRelationshipTypes.Sibling:
      return {
        patient,
        type: PatientRelationshipType.Sibling,
      };
    case PraktikaPatientRelationshipTypes.Spouse:
    case PraktikaPatientRelationshipTypes.Partner:
      return {
        patient,
        type: PatientRelationshipType.Partner,
      };
    case PraktikaPatientRelationshipTypes.Parent:
    case PraktikaPatientRelationshipTypes.StepParent:
    case PraktikaPatientRelationshipTypes.FosterParent:
      return {
        patient,
        type: PatientRelationshipType.Parent,
      };
    case PraktikaPatientRelationshipTypes.Child:
    case PraktikaPatientRelationshipTypes.FosterChild:
    case PraktikaPatientRelationshipTypes.StepChild:
      return {
        patient,
        type: PatientRelationshipType.Child,
      };
    default:
      return;
  }
}
